<template>
  <div id="reservation-confirmation" v-if="isLoading" class="loading-container">
    <Loader />
  </div>
  <div id="reservation-confirmation" v-else-if="reservation">
    <div class="header-section text-center py-5">
      <a href="/" class="back-link-confirmation">
        <div class="back-button d-flex align-items-center justify-content-center">
          <img class="ml-2" src="../../images/arrow-left-small.svg" alt="Back" style="width: 15px; height: 17.5px; transform: rotate(0deg);" />
          <span class="ml-2 raleway-semi-p14">Retour à l'accueil</span>
        </div>
      </a>
      
      <div class="success-icon mb-4">
        <div class="circle-check">
          <i class="fas fa-check"></i>
        </div>
      </div>
      
      <h1 :class="isPhone ? 'merriweather-reg-28' : 'merriweather-reg-40'" class="mb-3">
        {{ isOnDemandBooking ? 'Votre réservation est en attente de confirmation' : 'Votre réservation est confirmée' }}
      </h1>
      <p v-if="isOnDemandBooking" :class="isPhone ? 'raleway-reg-p14' : 'raleway-reg-p16'" class="text-danger mb-3">
        Vous recevrez un e-mail dans un délai maximum de 24 h pour vous informer si votre séjour est confirmé ou non.
      </p>
      <p v-else :class="isPhone ? 'merriweather-light-20' : 'merriweather-light-24'">
        Merci de voyager avec Hortense. Votre séjour est bien réservé, il ne vous reste plus<br>
        qu'à préparer votre escapade.
      </p>
    </div>

    <div class="divider mb-5"></div>

    <div class="main-container-confirmation pb-5 mx-auto">
      <div class="row">
        <!-- Left Section: Summary -->
        <div class="col-lg-7 pr-lg-4">
          <div class="summary-card p-4">
            <h2 class="mb-4" :class="isPhone ? 'merriweather-reg-24' : 'merriweather-reg-26'">Récapitulatif de votre réservation</h2>
            
            <div class="row">
              <div class="col-md-7">
                <h3 class="merriweather-reg-20 mb-2">{{ lodging.name }}</h3>
                <p class="raleway-reg-p13 mb-4">
                  {{ fullLocation() }}
                </p>
                
                <div class="details-list">
                  <div class="detail-item d-flex align-items-center mb-3">
                    <img src="../../images/icon_calendar.svg" class="mr-3" width="19" />
                    <span class="raleway-reg-p13">
                      {{ formatDate(reservation.checkin) }} &nbsp;→&nbsp; {{ formatDate(reservation.check_out) }}
                    </span>
                  </div>
                  
                  <div class="detail-item d-flex align-items-center mb-3">
                    <img src="../../images/profil.svg" class="mr-3" width="15" />
                    <span class="raleway-reg-p13 ml-1">
                      {{ reservation.adults }} adulte{{ reservation.adults > 1 ? 's' : '' }} - {{ reservation.childs }} enfant{{ reservation.childs > 1 ? 's' : '' }}
                    </span>
                  </div>
                  
                  <div class="detail-item d-flex align-items-center mb-3">
                    <img src="../../images/icon_moon.svg" class="mr-3" width="19" />
                    <span class="raleway-reg-p13">
                      {{ reservation.duration }} nuitée{{ reservation.duration > 1 ? 's' : '' }} - {{ (reservation.total / reservation.duration).toFixed(2) }}€ / nuit
                    </span>
                  </div>
                  
                  <div class="detail-item d-flex align-items-center mb-3">
                    <img src="../../images/icons8-home.svg" style="width: 19px;height: 19px;" />
                    <span class="raleway-reg-p13 ml-3">{{ lodgeTypeMapping[lodging.type] || lodging.type }}</span>
                  </div>
                  
                  <div class="detail-item d-flex align-items-center mb-4 text-green" v-if="lodging.green_experience">
                    <i class="fa-solid fa-gift text-green mr-3"></i>
                    <span class="raleway-semi-p13 ml-1">{{ lodging.green_experience }}</span>
                    <span 
                      class="green-privilege-container ml-1"
                      @mouseenter="showGreenPrivilegeTooltip = true"
                      @mouseleave="showGreenPrivilegeTooltip = false"
                    >
                      <img src="../../images/icons8-info-green.svg" style="width: 13px;height: 13px;" />
                      <div
                        v-if="showGreenPrivilegeTooltip"
                        class="green-privilege-tooltip raleway-reg-p13 mt-1"
                      >
                        Dès 4 nuits, un privilège exclusif offert par Hortense.
                      </div>
                    </span>
                  </div>
                </div>

                <div class="total-section d-flex justify-content-between align-items-center pt-4 border-top">
                  <span class="raleway-semi-p15">Montant total TTC</span>
                  <span class="raleway-semi-p15">{{ reservation.total.toFixed(2).replace('.', ',') }} EUR</span>
                </div>
              </div>
              
              <div class="col-md-5 d-none d-md-block">
                <img :src="lodging.photo_url" class="img-fluid rounded-lg lodging-img" />
              </div>
            </div>
            <p class="text-danger raleway-reg-p11 mt-3" v-if="reservation.cancel_conditions && reservation.cancel_conditions != ''">
              <img src="../../images/icons8-info-red.svg" style="width: 13px;height: 13px;" />
              <span class="ml-1">{{ reservation.cancel_conditions }}</span>
            </p>
            <p class="raleway-reg-p11 mt-3 text-danger">
              Un email récapitulatif a été envoyé à {{ reservation.user_email }}. Pensez à vérifier vos spams si vous ne le voyez pas apparaitre.
            </p>
          </div>
        </div>

        <!-- Right Section: Info & Club -->
        <div class="col-lg-5 mt-4 mt-lg-0">
          <!-- Payment Info -->
          <div class="info-card p-4 mb-4">
            <div class="d-flex align-items-center mb-2 text-danger">
              <img src="../../images/icons8-info-red.svg" style="width: 19px;height: 19px;" />
              <span class="raleway-semi-p11 text-uppercase ml-1">Informations sur le paiement</span>
            </div>
            <div v-if="paymentSchedule">
              <p class="raleway-reg-p11 text-danger mb-2">
                Votre réservation suit un calendrier de paiement. Voici les montants et les échéances prévus :
              </p>
              <div class="payment-schedule-summary">
                <div class="payment-schedule-item">
                  <p class="raleway-semi-p11 mb-1">Aujourd'hui</p>
                  <p class="raleway-reg-p11 mb-0">
                    En ligne, vous paierez <span class="raleway-semi-p11">{{ scheduleOnlineAmount }} EUR</span>
                    <span v-if="paymentSchedule.online_percentage > 0"> ({{ paymentSchedule.online_percentage }} %)</span>
                  </p>
                </div>

                <div
                  v-for="(item, index) in sortedScheduleItems"
                  :key="`payment-schedule-${item.id || index}`"
                  class="payment-schedule-item mt-3"
                >
                  <p class="raleway-semi-p11 mb-1">{{ scheduleItemDate(item) }}</p>
                  <p class="raleway-reg-p11 mb-0">
                    {{
                      item.offset_type === 'before_checkout'
                        ? `${item.days_offset} ${item.days_offset === 1 ? 'jour' : 'jours'} avant l'arrivée`
                        : `${item.days_offset} ${item.days_offset === 1 ? 'jour' : 'jours'} après la réservation`
                    }},
                    vous paierez <span class="raleway-semi-p11">{{ scheduleItemAmount(item) }} EUR</span>
                    ({{ item.percentage }} %)
                  </p>
                </div>

                <div v-if="scheduleRemainderPercentage > 0" class="payment-schedule-item mt-3">
                  <p class="raleway-semi-p11 mb-1">{{ formatScheduleDate(reservation.checkin) }}</p>
                  <p class="raleway-reg-p11 mb-0">
                    Sur place, vous paierez <span class="raleway-semi-p11">{{ scheduleRemainderAmount }} EUR</span>
                    (solde {{ scheduleRemainderPercentage }} %)
                  </p>
                </div>
              </div>
            </div>
            <p v-else class="raleway-reg-p11 text-danger mb-0">
              Vous réglerez votre séjour directement à votre arrivée à l'établissement. Une taxe de séjour, appliquée selon la réglementation locale, pourra être demandée sur place.
            </p>
          </div>

          <!-- Club Hortense -->
          <div class="club-card p-4">
            <div class="d-flex align-items-center mb-4 text-green">
              <img src="../../images/badge-trotter.png" style="width: 18px;height: 24px;"/>
              <span class="raleway-semi-p16 text-uppercase ml-2">Club Hortense</span>
            </div>
            
            <p class="raleway-reg-p15 mb-4">
              Félicitations, vous avez gagné <strong>{{ pointsEarned }} points</strong> sur votre statut Club Hortense.
            </p>

            <!-- Points Summary Section -->
            <div class="points-summary-section mb-4">
              <div class="points-summary-header">
                <span class="raleway-semi-p15 text-green text-uppercase">Vos points Club Hortense</span>
                <i class="fas fa-chevron-down"></i>
              </div>
              
              <div class="points-summary-content">
                <div class="points-row">
                  <span class="raleway-semi-p10 text-green text-uppercase">Actuellement</span>
                  <span class="raleway-medi-p10">{{ loyaltyUser ? loyaltyUser.loyalty_points : 0 }} points</span>
                </div>
                <div class="points-row">
                  <span class="raleway-semi-p10 text-green text-uppercase">Après votre séjour</span>
                  <span class="raleway-medi-p10">+ {{ pointsEarned }} points</span>
                </div>
                <div class="points-row total-row">
                  <span class="raleway-semi-p10 text-green text-uppercase">Total points après séjour</span>
                  <span class="raleway-semi-p10">{{ (loyaltyUser ? loyaltyUser.loyalty_points : 0) + pointsEarned }} points</span>
                </div>
              </div>
            </div>

            <p class="raleway-reg-p15 mb-2" v-if="reservation.service_exclusive && reservation.service_exclusive.length > 0">
              Vous bénéficiez, lors de votre séjour, de <span class="raleway-semi-p15 text-uppercase text-green">{{ reservation.service_exclusive.length }} privilège{{ reservation.service_exclusive.length > 1 ? 's' : '' }} exclusifs{{ reservation.service_exclusive.length > 1 ? 's' : '' }}</span>
            </p>
            
            <ul class="privilege-list" v-if="reservation.service_exclusive && reservation.service_exclusive.length > 0">
              <li
                v-for="(service, index) in reservation.service_exclusive"
                :key="index"
                class="active"
              >
                <i class="fas fa-check"></i>
                <span>{{ service }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import fetcher from "../../packs/shared/api";
import Loader from "../../components/shared/Loader.vue";

export default {
  name: "reservation-confirmation",
  components: {
    Loader
  },
  data() {
    return {
      reservationId: null,
      reservation: null,
      lodging: {},
      loyaltyUser: null,
      pointsEarned: 0,
      currentPrivilege: "Panier gourmand",
      showGreenPrivilegeTooltip: false,
      gaugeSteps: [],
      isLoading: true,
      lodgeTypeMapping: {
        "Hotel": 'Hôtels',
        "Maison et Villa": 'Maisons et Villas',
        "Guestroom": 'Chambres d\'hôtes',
        "Lodge": 'Lodges et lieux insolites',
        "Appartment": 'Appartements',
        "Chalet": 'Chalets'
      },
      isPhone: window.innerWidth < 768
    };
  },
  computed: {
    statusArrowStyle() {
      if (!this.loyaltyUser) return { left: '0%' };
      const points = this.loyaltyUser.loyalty_points || 0;
      let percentage = (points / 2500) * 100;
      if (percentage > 95) percentage = 95;
      return { left: `${percentage}%` };
    },
    isOnDemandBooking() {
      // Check if lodging has no lodging_channel_manager (no koedia)
      const hasNoKoedia = !this.lodging.lodging_channel_manager;
      
      // Check if lodging has no ical_url and no rooms have ical_url (no ical)
      // ical_url must be null, undefined, or empty string
      const hasNoIcal = (!this.lodging.ical_url || this.lodging.ical_url === '') &&
        (!this.lodging.rooms || this.lodging.rooms.length === 0 ||
         !this.lodging.rooms.some(room => room.ical_url && room.ical_url !== ''));
      return hasNoKoedia && hasNoIcal;
    },
    paymentSchedule() {
      return this.lodging?.payment_schedule || null;
    },
    sortedScheduleItems() {
      if (!this.paymentSchedule || !Array.isArray(this.paymentSchedule.items)) return [];
      return [...this.paymentSchedule.items].sort((a, b) => {
        if (a.offset_type === 'after_booking' && b.offset_type !== 'after_booking') return -1;
        if (a.offset_type !== 'after_booking' && b.offset_type === 'after_booking') return 1;
        if (a.offset_type === 'after_booking') return (a.days_offset || 0) - (b.days_offset || 0);
        return (b.days_offset || 0) - (a.days_offset || 0);
      });
    },
    scheduleOnlineAmount() {
      if (!this.paymentSchedule || !this.reservation) return '0.00';
      const pct = Number(this.paymentSchedule.online_percentage) || 0;
      const total = Number(this.reservation.total) || 0;
      return ((total * pct) / 100).toFixed(2);
    },
    scheduleRemainderPercentage() {
      if (!this.paymentSchedule) return 0;
      const itemsTotal = (this.paymentSchedule.items || []).reduce((sum, item) => sum + (Number(item.percentage) || 0), 0);
      return Math.max(0, 100 - (Number(this.paymentSchedule.online_percentage) || 0) - itemsTotal);
    },
    scheduleRemainderAmount() {
      if (!this.reservation) return '0.00';
      const total = Number(this.reservation.total) || 0;
      return ((total * this.scheduleRemainderPercentage) / 100).toFixed(2);
    }
  },
  async mounted() {
    localStorage.removeItem('reservationState');

    const urlParams = new URLSearchParams(window.location.search);
    this.reservationId = urlParams.get('reservation_id');

    if (this.reservationId) {
      await this.fetchData();
    }
  },
  methods: {
    fullLocation() {
      const capitalize = (str) => {
        if (!str) return '';
        return str
          .split('-') // split on hyphens
          .map(part =>
            part
              .split(' ') // split on spaces within each hyphen part
              .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(' ')
          )
          .join('-');
      };

      const { country, region, department } = this.lodging;

      return [country?.name, region?.name, department?.name]
        .filter(Boolean)
        .map(capitalize)
        .join(', ');
    },
    async fetchData() {
      try {
        const res = await fetcher.get(`/reservations/${this.reservationId}.json`);
        this.reservation = res.data;
        this.pointsEarned = Math.floor(this.reservation.duration * 50);
        
        // Mock lodging details based on reservation data or fetch if needed
        // Assuming lodging data comes with reservation or fetch separately
        const lodgingRes = await fetcher.get(`/lodgings/${this.reservation.lodging_slug}`);
        this.lodging = {
          name: lodgingRes.data.lodging.name,
          address: lodgingRes.data.lodging.address,
          zip_code: lodgingRes.data.lodging.zip_code,
          city_name: lodgingRes.data.lodging.city?.name || '',
          region_name: lodgingRes.data.lodging.region?.name || '',
          type_label: lodgingRes.data.lodging.type_label || '',
          green_experience: lodgingRes.data.lodging.lodging_description?.green_experience || '',
          photo_url: lodgingRes.data.lodging.photo_banner,
          ...lodgingRes.data.lodging
        };

        if (this.reservation.user_id) {
          const loyaltyRes = await fetcher.get(`/loyalty_status/${this.reservation.user_id}`);
          this.loyaltyUser = loyaltyRes.data;
        }
      } catch (e) {
        console.error("Error fetching reservation data:", e);
      } finally {
        this.isLoading = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const options = { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' };
      return date.toLocaleDateString('fr-FR', options);
    },
    formatScheduleDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    },
    scheduleItemDate(item) {
      if (!item || !this.reservation) return '';

      const baseDate = item.offset_type === 'before_checkout'
        ? new Date(this.reservation.checkin)
        : new Date(this.reservation.created_at || this.reservation.booking_date || this.reservation.checkin);

      if (Number.isNaN(baseDate.getTime())) return '';

      const offsetDays = Number(item.days_offset) || 0;
      const delta = offsetDays * 24 * 60 * 60 * 1000;
      const targetDate = item.offset_type === 'before_checkout'
        ? new Date(baseDate.getTime() - delta)
        : new Date(baseDate.getTime() + delta);

      return this.formatScheduleDate(targetDate);
    },
    scheduleItemAmount(item) {
      if (!this.reservation) return '0.00';
      const pct = Number(item?.percentage) || 0;
      const total = Number(this.reservation.total) || 0;
      return ((total * pct) / 100).toFixed(2);
    },
  }
};
</script>

<style lang="scss" scoped>
.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #fff;
}

#reservation-confirmation {
  background-color: #fff;
  min-height: 100vh;

  .back-link-confirmation {
    position: absolute;
    top: 12rem;
    left: 30px;
    text-decoration: none;
    color: #fff;
    
    .back-button {
      background: #707070;
      border-radius: 20px;
      padding: 8px 15px;
      img { filter: brightness(0) invert(1); transform: rotate(0deg); }
    }
  }

  .circle-check {
    width: 80px;
    height: 80px;
    border: 2px solid #2C5E4B;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #2C5E4B;
    font-size: 32px;
  }

  .divider {
    height: 1px;
    background: #E0E0E0;
    width: 80%;
    margin: 0 auto;
  }

  .summary-card, .info-card, .club-card {
    background: #FFFFFF;
    box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.05);
    border-radius: 15px;
    border: 1px solid #F0F0F0;
  }

  .lodging-img {
    height: 300px;
    width: 100%;
    object-fit: cover;
    border-radius: 10px;
  }

  .total-section {
    margin-top: 20px;
  }
}

@media (max-width: 768px) {
  #reservation-confirmation {
    margin-top: 8rem;
    .header-section {
      padding: 30px 15px;
    }
    .back-link-confirmation {
        top: 6rem;
        left: 10px;
    }
  }
}

.main-container-confirmation {
  width: 90%;
}

.green-privilege-container {
  position: relative;
  cursor: pointer;

  @media screen and (max-width: 768px) {
    position: unset;
  }
}

.green-privilege-tooltip {
  position: absolute;
  top: 100%;
  left: -10rem;
  right: 0;
  transform: translateY(8px);
  margin-top: 8px;
  padding: 12px 16px;
  background-color: #fff;
  color: #000;
  border: 2.44px solid #BFBFBF80;
  border-radius: 8px;
  white-space: normal;
  min-width: 280px;
  max-width: 400px;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  @media screen and (max-width: 768px) {
    left: 50%;
    transform: translateX(-50%) translateY(8px);
    max-width: min(320px, calc(100vw - 24px));
    margin-top: -1.5rem !important;
  }
}

.text-danger { color: #D56A70; }
.text-green { color: #3B6152; }

.points-summary-section {
  background-color: #E8F3EF;
  border-radius: 12px;
  overflow: hidden;
}

.points-summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background-color: #D4E8E0;
  cursor: pointer;
  user-select: none;
  
  i {
    color: #3B6152;
    font-size: 14px;
    transition: transform 0.3s ease;
  }
}

.points-summary-content {
  padding: 0 16px 16px 16px;
  background-color: #CFE5DC4E;
}

.points-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #CFE5DC;
  
  &:last-child {
    border-bottom: none;
  }
  
  &.total-row {
    padding-top: 16px;
    padding-bottom: 0;
    border-bottom: none;
  }
}

.privilege-list {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 0.85rem;

  li {
    display: flex;
    align-items: center;
    text-transform: uppercase;
    margin-bottom: 5px;

    &:last-child {
      margin-bottom: 0;
    }

    i {
      margin-right: 0.5rem;
      font-size: 1.1em;
      min-width: 15px; 
    }

    &.active {
      i {
        color: #3b6152;
      }
      span {
        color: inherit;
        font-family: 'Raleway', sans-serif;
        font-weight: 600;
        font-size: 11px;
        line-height: 20px;
        @media screen and (max-width: 480px) {
          line-height: 1.2rem;
        }
      }
    }

    &.locked {
      i {
        color: #999;
      }
      span {
        color: #999;
        font-family: 'Raleway', sans-serif;
        font-weight: 300;
        font-size: 11px;
        line-height: 20px;
        @media screen and (max-width: 480px) {
          line-height: 1.2rem;
        }
      }
    }
  }
}
</style>
